import angular, { IPromise } from 'angular'

import { ApiProduct } from '@vend/types'
import type {
  ModalFactory,
  ModalObject,
  VdToastNotificationService,
} from '../vend-ui/types'
import template from '../../templates/gift-cards/gift-cards.html'
import { workflows } from '../constants'
import { TrainingModeManager } from '../managers/training-mode-manager'
import { WebRegisterStatusManager } from '../managers/web-register-status-manager'
import { $q } from '../ng-compat/imports'
import { GiftCardRedeemed } from '../controllers/gift-cards/balances-gift-cards'

export type GiftCardAdded = {
  product: ApiProduct
  code: string
  value: number | string
  expiresAt: string | null
  isReload: boolean
  clientId: string
}

export type BalancesGiftCardsModal = ReturnType<
  ReturnType<typeof modalFactoryWithController>
>

/**
 * Returns a factory function used to generate a modal.
 *
 * @param {string} controller
 *                 The controller name.
 */
const modalFactoryWithController = (controller: string) => {
  return (
    Modal: ModalFactory,
    vdToastNotificationService: VdToastNotificationService,
    webRegisterStatusManager: WebRegisterStatusManager,
    trainingModeManager: TrainingModeManager
  ) => {
    'ngInject'
    /**
     * Provides a modal to manage gift cards.
     *
     * @class GiftCardsModal
     * @static
     */
    return {
      /**
       * Open the sell gift card modal.
       *
       * @method sellGiftCard
       *
       * @return {Promise} A promise resolved when the modal is closed
       */
      sellGiftCard() {
        return this._canSellGiftCards()
          .then(() =>
            Modal<GiftCardAdded>({
              template,
              controller,
              controllerAs: '$ctrl',
              workflow: workflows.SELLING,
            })
          )
          .then(modal => this._handleModalClose(modal))
      },

      /**
       * Open the redeem gift card modal.
       *
       * @method redeemGiftCard
       *
       * @param amount {Number}
       *        The amount requested to be redeemed
       *
       * @return {Promise} A promise resolved when the modal is closed
       */
      redeemGiftCard(amount: number) {
        return Modal<GiftCardRedeemed>({
          template,
          controller,
          controllerAs: '$ctrl',
          workflow: workflows.REDEEMING,
          amount,
        }).then(modal => this._handleModalClose(modal))
      },

      /**
       * Handles the 'closed' promise of a modal instance.
       *
       * @private
       * @method _handleModalClose
       *
       * @param modal {Modal}
       *        The Modal instance
       *
       * @return {IPromise} Angular promise resolved when the modal is closed
       */
      _handleModalClose<T>(modal: ModalObject<T>): IPromise<T> {
        return modal.closed.then((result: T) => {
          if (!result) {
            return $q.reject()
          }

          return result
        })
      },

      /**
       * Establish if gift cards can be sold.
       *
       * @private
       * @method _canSellGiftCards
       *
       * @return {Promise} A promise resolved if gift cards can be sold
       */
      _canSellGiftCards() {
        return $q((resolve, reject) => {
          if (trainingModeManager.trainingModeEnabled) {
            vdToastNotificationService.negative({
              message:
                'Gift cards can not be sold when the Sell screen is in training mode.',
            })
            return reject()
          }

          if (!webRegisterStatusManager.isOnline()) {
            vdToastNotificationService.negative({
              message:
                'Gift cards can not be sold when the Sell screen is offline.',
            })
            return reject()
          }

          return resolve()
        })
      },
    }
  }
}

/**
 * Provides a modal to manage gift cards via the Balances API.
 *
 * @module balancesGiftCardsModal
 */
angular
  .module('webregister.modals')
  .factory(
    'balancesGiftCardsModal',
    modalFactoryWithController('BalancesGiftCardsController')
  )

/**
 * Provides a modal to manage BigCommerce gift certs via GraphQL.
 *
 * @module bigcommerceGiftCertsModal
 */
angular
  .module('webregister.modals')
  .factory(
    'bigcommerceGiftCertsModal',
    modalFactoryWithController('BigCommerceGiftCertsController')
  )
